// Sun Clip Image Layering
// Converted from ES6 module to IIFE for Shopify compatibility
(function() {
    'use strict';

    window.SunClipImageLayering = function(canvasId) {
        this.canvas = document.getElementById(canvasId);
        this.ctx = this.canvas.getContext('2d');
        this.layers = new Map();
        this.baseImage = null;
        this.scale = 1;
        this.offsetX = 0;
        this.offsetY = 0;
        this.currentScale = 1;
        this.currentOffsetX = 0;
        this.currentOffsetY = 0;
        this.lockYAxis = false; // Enable full X and Y axis movement
        this.initialScale = null; // Store initial scale to limit zoom out
        this.userHasInteracted = false; // Track if user has manually adjusted viewport
        this.imageCache = new Map();
        this.preloadedImages = new Set();
        this.loadingImages = new Set();

        this.setupCanvas();
        this.setupEventListeners();
        this.startPreloading();

        // Cache container dimensions to prevent unnecessary resizes
        this.lastContainerWidth = 0;
        this.lastContainerHeight = 0;
        this.isResizing = false;

        return this;
    };

    window.SunClipImageLayering.prototype = {
        setupCanvas: function() {
            // Set canvas size based on container with high-DPI support
            this.pixelRatio = window.devicePixelRatio || 1;
            this.resizeCanvas();

            const self = this;
            // Add debouncing to resize events to prevent glitchy behavior
            // FIX: Use longer debounce on mobile to prevent layout thrashing during transitions
            let resizeTimeout;
            const isMobile = window.innerWidth <= 768;
            const debounceDelay = isMobile ? 300 : 100; // 300ms on mobile, 100ms on desktop
            window.addEventListener('resize', function() {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(function() {
                    self.resizeCanvas();
                }, debounceDelay);
            });
        },

        setupEventListeners: function() {
            // Add mouse/touch events for pan and zoom
            let isMouseDown = false;
            let lastX = 0;
            let lastY = 0;
            const self = this;

            this.canvas.addEventListener('mousedown', function(e) {
                isMouseDown = true;
                lastX = e.clientX;
                lastY = e.clientY;
                self.canvas.style.cursor = 'grabbing';
            });

            this.canvas.addEventListener('mousemove', function(e) {
                if (isMouseDown) {
                    const deltaX = e.clientX - lastX;
                    const deltaY = e.clientY - lastY;
                    self.pan(deltaX, deltaY);
                    lastX = e.clientX;
                    lastY = e.clientY;
                }
            });

            this.canvas.addEventListener('mouseup', function() {
                isMouseDown = false;
                self.canvas.style.cursor = 'grab';
            });

            this.canvas.addEventListener('mouseleave', function() {
                isMouseDown = false;
                self.canvas.style.cursor = 'default';
            });

            this.canvas.addEventListener('wheel', function(e) {
                e.preventDefault();
                const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;

                // Use horizontal position from mouse, but center Y if Y-axis is locked
                const zoomX = e.offsetX;
                const zoomY = self.lockYAxis ? (self.canvas.height / self.pixelRatio) / 2 : e.offsetY;

                self.zoom(zoomFactor, zoomX, zoomY);
            });

            // Touch events for mobile
            this.setupTouchEvents();
        },

        setupTouchEvents: function() {
            // MOBILE FIX: Disable touch pan/zoom on mobile (≤768px) to allow native scrolling
            // Touch interactions block scroll propagation via e.preventDefault()
            // On mobile, scrolling the selection-section is more important than canvas pan/zoom
            if (window.innerWidth <= 768) {
                console.log('[Canvas] Touch events disabled on mobile for scroll compatibility');
                return; // Skip touch event setup on mobile
            }

            let touchStartDistance = 0;
            let touchStartScale = 1;
            let lastTouchX = 0;
            let lastTouchY = 0;
            const self = this;

            this.canvas.addEventListener('touchstart', function(e) {
                e.preventDefault();
                if (e.touches.length === 1) {
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                } else if (e.touches.length === 2) {
                    touchStartDistance = self.getTouchDistance(e.touches);
                    touchStartScale = self.scale;
                }
            });

            this.canvas.addEventListener('touchmove', function(e) {
                e.preventDefault();
                if (e.touches.length === 1) {
                    const deltaX = e.touches[0].clientX - lastTouchX;
                    const deltaY = e.touches[0].clientY - lastTouchY;
                    self.pan(deltaX, deltaY);
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                } else if (e.touches.length === 2) {
                    const currentDistance = self.getTouchDistance(e.touches);
                    const zoomFactor = currentDistance / touchStartDistance;
                    self.setScale(touchStartScale * zoomFactor);
                }
            });
        },

        getTouchDistance: function(touches) {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        },

        resizeCanvas: function() {
            // Prevent resize during sticky state changes or if already resizing
            if (this.isResizing || (window.sunClipConfigurator && window.sunClipConfigurator.isChangingSticky)) {
                return;
            }

            // FIX: Mobile throttle - prevent rapid resize calls during step transitions
            const now = Date.now();
            const isMobile = window.innerWidth <= 768;
            const minInterval = isMobile ? 200 : 50; // 200ms minimum between resizes on mobile
            if (this._lastResizeTime && (now - this._lastResizeTime) < minInterval) {
                return;
            }
            this._lastResizeTime = now;

            // Prevent resize if viewport width hasn't actually changed (prevents layout thrashing)
            if (window.sunClipConfigurator && window.sunClipConfigurator.lastViewportWidth) {
                const currentViewportWidth = window.innerWidth;
                if (Math.abs(currentViewportWidth - window.sunClipConfigurator.lastViewportWidth) < 5) {
                    // Small viewport changes shouldn't trigger canvas resize
                    return;
                }
                window.sunClipConfigurator.lastViewportWidth = currentViewportWidth;
            }

            const container = this.canvas.parentElement;
            const rect = container.getBoundingClientRect();

            // Only resize if dimensions actually changed (increased threshold)
            if (Math.abs(rect.width - this.lastContainerWidth) < 5 &&
                Math.abs(rect.height - this.lastContainerHeight) < 5) {
                return;
            }

            this.isResizing = true;

            // Cache the new dimensions
            this.lastContainerWidth = rect.width;
            this.lastContainerHeight = rect.height;

            // Set display size (CSS pixels)
            this.canvas.style.width = rect.width + 'px';
            this.canvas.style.height = rect.height + 'px';

            // Set actual size for high-DPI displays
            this.canvas.width = rect.width * this.pixelRatio;
            this.canvas.height = rect.height * this.pixelRatio;

            // Scale the drawing context to match device pixel ratio
            this.ctx.scale(this.pixelRatio, this.pixelRatio);

            // Ensure crisp rendering
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';

            this.render();

            // Allow resizing again after a short delay
            const self = this;
            setTimeout(function() {
                self.isResizing = false;
            }, 50);
        },

        loadImage: function(src, showProgress) {
            const self = this;

            // Return cached image immediately
            if (this.imageCache.has(src)) {
                return Promise.resolve(this.imageCache.get(src));
            }

            // Prevent duplicate loading of the same image
            if (this.loadingImages.has(src)) {
                // Wait for the existing load to complete
                return new Promise(function(resolve) {
                    const checkForImage = function() {
                        if (self.imageCache.has(src)) {
                            resolve(self.imageCache.get(src));
                        } else if (self.loadingImages.has(src)) {
                            setTimeout(checkForImage, 50);
                        }
                    };
                    checkForImage();
                });
            }

            this.loadingImages.add(src);

            // Timeout for slow mobile networks (LTE) - 10 seconds default
            var timeout = 10000;

            return new Promise(function(resolve, reject) {
                const img = new Image();
                var timeoutId = null;
                var settled = false;

                // Add loading progress if requested
                if (showProgress) {
                    self.updateLoadingMessage('Loading ' + src.split('/').pop() + '...');
                }

                // Set up timeout for slow networks
                timeoutId = setTimeout(function() {
                    if (!settled) {
                        settled = true;
                        console.warn('Image load timeout after ' + timeout + 'ms: ' + src);
                        self.loadingImages.delete(src);
                        img.src = ''; // Cancel the image load
                        reject(new Error('Timeout loading image: ' + src));
                    }
                }, timeout);

                img.onload = function() {
                    if (!settled) {
                        settled = true;
                        clearTimeout(timeoutId);
                        self.imageCache.set(src, img);
                        self.loadingImages.delete(src);
                        self.preloadedImages.add(src);
                        resolve(img);
                    }
                };

                img.onerror = function() {
                    if (!settled) {
                        settled = true;
                        clearTimeout(timeoutId);
                        console.error('Failed to load image: ' + src);
                        self.loadingImages.delete(src);
                        reject(new Error('Failed to load image: ' + src));
                    }
                };

                img.src = src;
            });
        },

        loadBaseImage: function(src) {
            const self = this;
            this.showLoading();

            this.loadImage(src, true).then(function(image) {
                self.baseImage = image;
                self.fitToCanvas();

                // Ensure image is properly centered after loading
                if (self.lockYAxis) {
                    self.centerImage();
                }

                self.render();
                self.hideLoading();
            }).catch(function(error) {
                console.error('Error loading base image:', error);
                self.hideLoading();
            });
        },

        updateLayer: function(layerId, src, zIndex) {
            const self = this;
            zIndex = zIndex || 1;

            // VALIDATION: Check if src is a valid Shopify CDN URL
            // Invalid URLs: just filename without path, contains 'undefined' or 'null'
            if (!src || typeof src !== 'string') {
                console.warn('[Canvas] Invalid src for layer ' + layerId + ': empty or not a string');
                return;
            }
            if (src.indexOf('undefined') !== -1 || src.indexOf('null') !== -1) {
                console.warn('[Canvas] Invalid src for layer ' + layerId + ': contains undefined/null -', src);
                return;
            }
            // Check for valid asset URL formats:
            // - Full URL: https://cdn.shopify.com/...
            // - Protocol-relative: //chemistrie.com/cdn/shop/... (production)
            // - Relative CDN path: /cdn/shop/... (local dev)
            // - Any other URL with protocol: ://
            var isValidUrl = src.indexOf('cdn.shopify.com') !== -1 ||
                             src.indexOf('/cdn/') !== -1 ||  // Contains /cdn/ anywhere
                             src.indexOf('://') !== -1 ||
                             src.indexOf('//') === 0;  // Protocol-relative URL
            if (!isValidUrl) {
                console.warn('[Canvas] Asset URL not resolved for layer ' + layerId + ':', src);
                // Skip loading - this prevents 404 errors on slow networks
                return;
            }

            // Check if image is already cached - no need to show loading
            const isPreloaded = this.imageCache.has(src);

            if (!isPreloaded) {
                this.showLoading();
            }

            this.loadImage(src, !isPreloaded).then(function(image) {
                self.layers.set(layerId, { image: image, zIndex: zIndex });
                self.render();
                if (!isPreloaded) {
                    self.hideLoading();
                }
            }).catch(function(error) {
                console.error('Error updating layer ' + layerId + ':', error);
                // Only show error to user if it's not a timeout (timeout already logged)
                if (error.message && error.message.indexOf('Timeout') === -1) {
                    self.showErrorMessage('Failed to load ' + layerId + ' image');
                }
                if (!isPreloaded) {
                    self.hideLoading();
                }
            });
        },

        removeLayer: function(layerId) {
            this.layers.delete(layerId);
            this.render();
        },

        clearAllLayers: function() {
            this.layers.clear();
            this.render();
        },

        updateClipType: function(clipType) {
            // For now, we'll keep the same base image for all clip types
            // In the future, this could load different base images for different clip types

            // Clear lens layer when switching clip types
            this.removeLayer('lens');

            // You could add different base images or lens overlays here
            // based on the clip type if needed
        },

        fitToCanvas: function() {
            if (!this.baseImage) return;
            // Default to full glasses view
            this.setViewport('full');
        },

        centerImage: function() {
            if (!this.baseImage) return;

            // Account for device pixel ratio when centering
            const canvasWidth = this.canvas.width / this.pixelRatio;
            const canvasHeight = this.canvas.height / this.pixelRatio;

            this.offsetX = (canvasWidth - this.baseImage.width * this.scale) / 2;
            this.offsetY = (canvasHeight - this.baseImage.height * this.scale) / 2;

            this.render();
        },

        setViewport: function(viewType) {
            if (!this.baseImage) return;

            // Reset user interaction flag since this is a programmatic viewport change
            this.userHasInteracted = false;

            // Define viewport regions based on coordinates (scaled for 2000x1500 images)
            const viewports = {
                'full': { // Starting position - full glasses view
                    x1: 57, y1: 417, x2: 1918, y2: 1102
                },
                'bridge': { // Bridge selection - coordinates for all layers including bridge (moved lower)
                    x1: 998, y1: 504, x2: 1710, y2: 940
                },
                'magnet': { // Magnet selection - matched to crystal viewport for consistency
                    x1: 526, y1: 344, x2: 1990, y2: 1077
                },
                'crystal': { // Crystal selection - updated coordinates for better crystal view
                    x1: 526, y1: 344, x2: 1990, y2: 1077
                }
            };

            const viewport = viewports[viewType] || viewports['full'];

            // Calculate the center and dimensions of the viewport
            const centerX = (viewport.x1 + viewport.x2) / 2;
            const centerY = (viewport.y1 + viewport.y2) / 2;
            const viewWidth = Math.abs(viewport.x2 - viewport.x1);
            const viewHeight = Math.abs(viewport.y2 - viewport.y1);

            // Calculate scale to fit the viewport in the canvas
            const canvasWidth = this.canvas.width / this.pixelRatio;
            const canvasHeight = this.canvas.height / this.pixelRatio;

            const scaleX = canvasWidth / viewWidth;
            const scaleY = canvasHeight / viewHeight;

            // Use the smaller scale to ensure the entire viewport fits
            // Add padding for better framing - increased for better visibility
            this.scale = Math.min(scaleX, scaleY) * 0.95;

            // Store the default full scale for zoom out reference
            if (viewType === 'full' && !this.defaultFullScale) {
                this.defaultFullScale = this.scale;
            }

            // Store the initial scale when first setting the viewport (for zoom limit)
            if (!this.initialScale) {
                this.initialScale = this.scale;
            }

            // Calculate offsets to center the viewport
            this.offsetX = (canvasWidth / 2) - (centerX * this.scale);

            // For Y-axis: if locked, always center the image vertically
            if (this.lockYAxis) {
                this.offsetY = (canvasHeight - this.baseImage.height * this.scale) / 2;
            } else {
                this.offsetY = (canvasHeight / 2) - (centerY * this.scale);
            }

            // Animate the transition (current values will be updated during animation)
            this.animateViewportChange();
        },

        animateViewportChange: function(targetScale, targetOffsetX, targetOffsetY) {
            // Use provided targets or current values as fallback
            targetScale = targetScale !== undefined ? targetScale : this.scale;
            targetOffsetX = targetOffsetX !== undefined ? targetOffsetX : this.offsetX;
            targetOffsetY = targetOffsetY !== undefined ? targetOffsetY : this.offsetY;

            // Get current values
            const startScale = this.currentScale || this.scale;
            const startOffsetX = this.currentOffsetX || this.offsetX;
            const startOffsetY = this.currentOffsetY || this.offsetY;

            // Animation duration - reduced for smoother transitions
            const duration = 300; // ms
            const startTime = Date.now();
            const self = this;

            const animate = function() {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function for smooth animation
                const easeProgress = self.easeInOutCubic(progress);

                // Interpolate values
                self.scale = startScale + (targetScale - startScale) * easeProgress;
                self.offsetX = startOffsetX + (targetOffsetX - startOffsetX) * easeProgress;
                self.offsetY = startOffsetY + (targetOffsetY - startOffsetY) * easeProgress;

                // Store current values for next animation
                self.currentScale = self.scale;
                self.currentOffsetX = self.offsetX;
                self.currentOffsetY = self.offsetY;

                self.render();

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        },

        easeInOutCubic: function(t) {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        },

        pan: function(deltaX, deltaY) {
            // Track user interaction
            this.userHasInteracted = true;

            // Calculate new position
            const newOffsetX = this.offsetX + deltaX;
            const newOffsetY = this.lockYAxis ? this.offsetY : this.offsetY + deltaY; // Respect Y-axis lock

            // Allow more freedom for panning with gentle boundaries
            if (this.baseImage) {
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageWidth = this.baseImage.width * this.scale;
                const imageHeight = this.baseImage.height * this.scale;

                // Allow 30% overflow for better viewing of edge details
                const overflowX = imageWidth * 0.3;
                const overflowY = imageHeight * 0.3;

                // More permissive boundaries
                const minOffsetX = canvasWidth - imageWidth - overflowX;
                const minOffsetY = canvasHeight - imageHeight - overflowY;
                const maxOffsetX = overflowX;
                const maxOffsetY = overflowY;

                // Apply gentler boundaries
                this.offsetX = Math.min(maxOffsetX, Math.max(minOffsetX, newOffsetX));

                // Only update Y position if Y-axis is not locked
                if (!this.lockYAxis) {
                    this.offsetY = Math.min(maxOffsetY, Math.max(minOffsetY, newOffsetY));
                }
            } else {
                this.offsetX = newOffsetX;
                if (!this.lockYAxis) {
                    this.offsetY = newOffsetY;
                }
            }

            this.render();
        },

        zoom: function(factor, centerX, centerY, programmatic) {
            // Track user interaction (unless this is a programmatic zoom)
            if (!programmatic) {
                this.userHasInteracted = true;
            }

            const oldScale = this.scale;
            const minScale = this.initialScale || 0.5; // Use initial scale as minimum zoom limit
            const newScale = Math.max(minScale, Math.min(5, this.scale * factor));

            // If scale hasn't changed, don't do anything
            if (Math.abs(newScale - oldScale) < 0.001) return;

            // Calculate scale change
            const scaleChange = newScale / oldScale;

            // Update scale first
            this.scale = newScale;

            // Adjust offset to zoom towards center point
            this.offsetX = centerX - (centerX - this.offsetX) * scaleChange;

            // Apply Y-axis lock - maintain vertical centering if locked
            if (this.lockYAxis) {
                // Keep image vertically centered when Y-axis is locked
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageHeight = this.baseImage.height * this.scale;
                this.offsetY = (canvasHeight - imageHeight) / 2;
            } else {
                this.offsetY = centerY - (centerY - this.offsetY) * scaleChange;
            }

            // Apply gentle boundaries with overflow allowance
            if (this.baseImage) {
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const imageWidth = this.baseImage.width * this.scale;
                const imageHeight = this.baseImage.height * this.scale;

                // Allow overflow for better detail viewing
                const overflowX = imageWidth * 0.3;
                const overflowY = imageHeight * 0.3;

                // Center the image if it's smaller than canvas
                if (imageWidth < canvasWidth) {
                    this.offsetX = (canvasWidth - imageWidth) / 2;
                } else {
                    // More permissive boundaries for zoomed images
                    const maxOffsetX = overflowX;
                    const minOffsetX = canvasWidth - imageWidth - overflowX;
                    this.offsetX = Math.min(maxOffsetX, Math.max(minOffsetX, this.offsetX));
                }

                if (imageHeight < canvasHeight) {
                    this.offsetY = (canvasHeight - imageHeight) / 2;
                } else if (!this.lockYAxis) {
                    // Only apply Y boundaries if Y-axis is not locked
                    const maxOffsetY = overflowY;
                    const minOffsetY = canvasHeight - imageHeight - overflowY;
                    this.offsetY = Math.min(maxOffsetY, Math.max(minOffsetY, this.offsetY));
                }
                // If Y-axis is locked and image is larger than canvas, keep the calculated center position
            }

            this.render();
        },

        setScale: function(newScale) {
            const minScale = this.initialScale || 0.5; // Use initial scale as minimum zoom limit
            this.scale = Math.max(minScale, Math.min(5, newScale));
            this.render();
        },

        resetView: function() {
            // Reset user interaction flag since user is requesting default view
            this.userHasInteracted = false;

            // Reset to current step's default viewport
            if (window.orderFlowConfigurator && window.orderFlowConfigurator.applyStepViewport) {
                window.orderFlowConfigurator.applyStepViewport(window.orderFlowConfigurator.currentStep);
            } else if (window.sunClipConfigurator) {
                window.sunClipConfigurator.updateViewport();
            } else {
                this.setViewport('full');
            }
        },

        render: function() {
            // Clear canvas (account for device pixel ratio)
            this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

            // Save context for transformations
            this.ctx.save();

            // Apply transformations
            this.ctx.translate(this.offsetX, this.offsetY);
            this.ctx.scale(this.scale, this.scale);

            // Enable high-quality rendering
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';

            // Render base image
            if (this.baseImage) {
                this.ctx.drawImage(this.baseImage, 0, 0);
            }

            // Render layers in order of zIndex
            const sortedLayers = Array.from(this.layers.entries())
                .sort(function(a, b) { return a[1].zIndex - b[1].zIndex; });

            for (let i = 0; i < sortedLayers.length; i++) {
                const layer = sortedLayers[i][1];
                if (layer.image) {
                    this.ctx.drawImage(layer.image, 0, 0);
                }
            }

            // Restore context
            this.ctx.restore();
        },

        generatePreview: function() {
            const self = this;

            return new Promise(function(resolve) {
                // Create a temporary canvas for preview generation
                const previewCanvas = document.createElement('canvas');
                const previewCtx = previewCanvas.getContext('2d');

                if (!self.baseImage) {
                    resolve(null);
                    return;
                }

                // Set canvas size to match base image
                previewCanvas.width = self.baseImage.width;
                previewCanvas.height = self.baseImage.height;

                // Draw base image
                previewCtx.drawImage(self.baseImage, 0, 0);

                // Draw layers
                const sortedLayers = Array.from(self.layers.entries())
                    .sort(function(a, b) { return a[1].zIndex - b[1].zIndex; });

                for (let i = 0; i < sortedLayers.length; i++) {
                    const layer = sortedLayers[i][1];
                    if (layer.image) {
                        previewCtx.drawImage(layer.image, 0, 0);
                    }
                }

                // Convert to base64
                resolve(previewCanvas.toDataURL('image/png', 0.9));
            });
        },

        showLoading: function() {
            const loadingOverlay = document.getElementById('loadingOverlay');
            if (loadingOverlay) {
                loadingOverlay.classList.add('active');
            }
        },

        hideLoading: function() {
            const loadingOverlay = document.getElementById('loadingOverlay');
            if (loadingOverlay) {
                loadingOverlay.classList.remove('active');
            }
        },

        showErrorMessage: function(message) {
            // Create error message element
            const errorDiv = document.createElement('div');
            errorDiv.className = 'error-message';
            errorDiv.textContent = message;
            errorDiv.style.cssText = `
                position: fixed;
                top: 20px;
                right: 20px;
                background-color: #f44336;
                color: white;
                padding: 15px 20px;
                border-radius: 4px;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                z-index: 1000;
                animation: slideIn 0.3s ease;
            `;

            document.body.appendChild(errorDiv);

            setTimeout(function() {
                errorDiv.remove();
            }, 5000);
        },

        // Preloading system for better performance
        startPreloading: function() {
            // Preload most commonly used images in background
            this.preloadCommonImages();
        },

        preloadCommonImages: function() {
            const self = this;

            // Wait for sunClipConfiguratorData to be available
            const waitForData = function() {
                if (window.sunClipConfiguratorData) {
                    const configuratorData = window.sunClipConfiguratorData;
                    const imagesToPreload = [];

                    // Add most popular lens colors (first few from each category)
                    ['solid', 'mirror', 'gradient'].forEach(function(category) {
                        if (configuratorData.lensColors[category]) {
                            configuratorData.lensColors[category].slice(0, 3).forEach(function(color) {
                                // Convert template URL to actual URL - this needs to be handled differently in Shopify
                                const fileName = 'Raen-Angle-Lens-' + color.id + '.png';
                                // For now, we'll skip preloading since we can't resolve Shopify asset URLs in JS
                                // imagesToPreload.push(fileName);
                            });
                        }
                    });

                } else {
                    setTimeout(waitForData, 100);
                }
            };

            setTimeout(waitForData, 1000);
        },

        updateLoadingMessage: function(message) {
            const loadingOverlay = document.querySelector('.loading-overlay');
            const existingMessage = loadingOverlay && loadingOverlay.querySelector('.loading-message');

            if (loadingOverlay && existingMessage) {
                existingMessage.textContent = message;
            } else if (loadingOverlay) {
                const messageDiv = document.createElement('div');
                messageDiv.className = 'loading-message';
                messageDiv.textContent = message;
                messageDiv.style.cssText = `
                    margin-top: 10px;
                    font-size: 0.9rem;
                    color: #666;
                `;
                loadingOverlay.appendChild(messageDiv);
            }
        },

        // Utility methods for external controls
        zoomIn: function() {
            const centerX = (this.canvas.width / this.pixelRatio) / 2;
            const centerY = (this.canvas.height / this.pixelRatio) / 2;
            this.zoom(1.1, centerX, centerY);
        },

        zoomOut: function() {
            const centerX = (this.canvas.width / this.pixelRatio) / 2;
            const centerY = (this.canvas.height / this.pixelRatio) / 2;

            // Get the default scale for full view
            if (!this.defaultFullScale && this.baseImage) {
                // Calculate what the full view scale would be (scaled for 2000x1500 images)
                const viewports = {
                    'full': { x1: 57, y1: 417, x2: 1918, y2: 1102 }
                };
                const viewport = viewports['full'];
                const viewWidth = Math.abs(viewport.x2 - viewport.x1);
                const viewHeight = Math.abs(viewport.y2 - viewport.y1);
                const canvasWidth = this.canvas.width / this.pixelRatio;
                const canvasHeight = this.canvas.height / this.pixelRatio;
                const scaleX = canvasWidth / viewWidth;
                const scaleY = canvasHeight / viewHeight;
                this.defaultFullScale = Math.min(scaleX, scaleY) * 0.95;
            }

            // Allow zooming out beyond the default view
            if (this.defaultFullScale && Math.abs(this.scale - this.defaultFullScale) < 0.05) {
                // If we're very close to default scale, make bigger jump to go past it
                this.zoom(0.75, centerX, centerY);
            } else {
                // Normal zoom out
                this.zoom(0.9, centerX, centerY);
            }
        }
    };

    // Compatibility aliases
    window.ImageLayering = window.SunClipImageLayering;  // Legacy alias
    window.ConfiguratorCanvas = window.SunClipImageLayering;  // New generic name (renamed from sun-clip-image-layering.js)

})();